<template>
  <v-form id="paymentDetails" ref="form" lazy-validation v-model="isFormValid" dense>
    <h2>Payment Details</h2>
    <v-alert type="error" v-if="response_errors">
      {{response_errors}}
    </v-alert>
    <v-alert type="success" v-if="promoCode">
      Promotion code successfully applied
    </v-alert>
    <v-row>
      <v-col>
        <table class="summary-table">
            <tbody>
              <tr v-if="promoCode">
                <td>{{package_name}}</td>
                <td>
                  ${{(promoCode.firstMonthCharge * 1.1).toFixed(2)}}<span v-if="!is_annual"></span>
                  <span v-if="promoCode.firstMonthCharge !== promoCode.standardMonthlyCharge"> initially and ${{(promoCode.standardMonthlyCharge * 1.1).toFixed(2)}} thereafter</span>
                </td>
              </tr>
              <tr v-else>
                <td>{{package_name}}</td>
                <td>${{(plan_cost * 1.1).toFixed(2)}}<span v-if="!is_annual"> per month</span></td>
              </tr>
              <tr :key="option_idx" v-for="(option, option_idx) in service_options">
                <td v-if="additional_services[option_idx]">
                  {{option.frontend_name}}
                </td>
                <td v-if="additional_services[option_idx]">
                  <span v-if="option.setup_charge">${{(option.setup_charge * 1.1).toFixed(2)}} Setup</span><span v-if="option.setup_charge && option.recurring_charge">, </span><span v-if="option.recurring_charge">${{(option.recurring_charge * 1.1).toFixed(2)}}
                    <span v-if="option.charge_frequency == 30">per month</span>
                    <span v-if="option.charge_frequency == 91">per quarter</span>
                    <span v-if="option.charge_frequency == 182">per half yearly</span>
                    <span v-if="option.charge_frequency == 365">per year</span>
                  </span>
                </td>
              </tr>
              <tr v-if="promoCode && promoCode.setupCharge > 0">
                <td>Promotion Setup Charge</td>
                <td>${{(promoCode.setupCharge * 1.1).toFixed(2)}}</td>
              </tr>
              <tr>
                <td>Recurring Cost</td>
                <td>${{total_recurring_charge}}</td>
              </tr>
              <tr>
                <td>Total for this transaction</td>
                <td>${{total_transaction_charge}}</td>
              </tr>
            </tbody>
        </table>
      </v-col>
    </v-row>
    <p>
      <strong>All charges inc GST</strong>
    </p>
    <p>Please enter your credit card details. <strong>All fields required.</strong><br />
    Visa/MasterCard attract a 1.5% surcharge and 2.42% for AMEX.
    </p>

    <v-row>
      <v-col>
        <v-text-field required solo dense 
          name="card_holder_name" 
          v-model="card_name" 
          placeholder="Card Holder Name"
          :rules="rules.card_holder_name"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-text-field required solo dense
          name="card_number" 
          v-model="card_number" 
          placeholder="Card Number" 
          v-mask="'####-####-####-####'"
          :rules="rules.card_number"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-text-field solo dense required 
          v-mask="'##/##'" 
          name="card_expiry" 
          v-model="card_expiry" 
          autocomplete="cc-exp" 
          placeholder="MM/YY"
          :rules="rules.card_expiry"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field required solo dense 
          name="card_cvv" 
          v-model="card_cvv" 
          placeholder="Card verification" 
          v-mask="'####'"
          :rules="rules.card_verification"
        ></v-text-field>
      </v-col>
    </v-row>
    <h4>Billing Address:</h4>
    <MailingComponent storedName="BillingAddress"></MailingComponent>
    <v-divider class="bottom-divider mt-3"></v-divider>
    <v-row class="pt-5">
      <v-col class="col-md-5 offset-md-5">
        <v-text-field v-model="promocode" solo dense placeholder="Promotion Code"></v-text-field>
      </v-col>
      <v-col class="col-md-2">
        <v-btn solo dense class="float-right" @click="attemptPromoCode">Apply</v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-checkbox v-model="agreement">
          <template v-slot:label>
            <div>
              I agree to the
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <a
                    target="_blank"
                    href="https://clarenceprofessionalgroup.com.au/terms-and-conditions/"
                    @click.stop
                    v-on="on"
                  >
                    Terms and Conditions
                  </a>
                </template>
                Opens in new window
              </v-tooltip>
            </div>
          </template>
        </v-checkbox>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-btn @click="goBack">Go back</v-btn>
      </v-col>
      <v-col>
        <v-btn @click="validate" class="float-right" :loading="submission_loading" :disabled="!isFormValid || isDisabled" >Signup</v-btn>
      </v-col>
    </v-row>

  </v-form>
</template>

<script>
  import {mapState, mapGetters} from "vuex";
  import MailingComponent from "./MailingComponent";
  import EwayErrors from '@/plugins/eway_errors';
  export default {
    name: 'PaymentDetails',
    components: {
      MailingComponent,
    },
    data: function() {
      return {
        isFormValid: false,
        checkbox: false,
        promocode: null,
        mail_input_option: null,
        menu: false,
        agreement: false,
        response_errors: null,
        rules: {
          card_holder_name: [
            v => !!v || this.$t('payment.card.holder_name.required'),
            ],
          card_number: [
            v => !!v || this.$t('payment.card.number.required'),
            ],
          card_expiry: [
            v => !!v || this.$t('payment.card.expiry.required'),
            ],
          card_verification: [
            v => !!v || this.$t('payment.card.cvv.required'),
            ],
          agreement_required: [
            v => !!v || this.$t('payment.agreement.required'),
            v => v != false || this.$t('payment.agreement.required'),
            ],
        },
      }
    },
    computed: {
      ...mapState('additional_services',{
        additional_services: state => state,
      }),

      ...mapState({
        package_name: state => state.service_data.selected.name.replace(/_/g, ' ').trim().toLowerCase().split(' ').map((s) => s.charAt(0).toUpperCase() + s.substring(1)).join(' '),
        service_options: state => state.service_data.selected.service_options,
      }),
      ...mapGetters('service_data', [
        'is_annual',
        'plan_cost',
      ]),
      ...mapState('service_data', [
        'submission_loading',
        'promoCode',
      ]),
      isDisabled() {
        return !this.agreement;
      },
      total_recurring_charge: function() {
        const sum_charges = [];
        const selected_state = this.$store.state.service_data.selected;
        const additional_services_state = this.$store.state.additional_services;
        if (selected_state.service_options) {
          Object.keys(selected_state.service_options).forEach(function(key) {
            if (additional_services_state[key]) {
              sum_charges.push((selected_state.service_options[key].recurring_charge));
            }
          });
        }
        // add the plan to the sum of charges
        if(this.$store.state.service_data.promoCode) {
          // Promotion calculate the forward cost
          sum_charges.push(Number(this.$store.state.service_data.promoCode.standardMonthlyCharge));
        } else {
          sum_charges.push(Number(selected_state.price));
        }
        return (sum_charges.reduce((total, num) => total + num) * 1.1).toFixed(2);
      },
      total_transaction_charge: function() {
        const sum_charges = [];
        const selected_state = this.$store.state.service_data.selected;
        const additional_services_state = this.$store.state.additional_services;
        if (selected_state.service_options) {
          Object.keys(selected_state.service_options).forEach(function(key) {
            if (additional_services_state[key]) {
              sum_charges.push((selected_state.service_options[key].setup_charge) + (selected_state.service_options[key].recurring_charge));
            }
          });
        }
        // add the plan to the sum of charges
        if(this.$store.state.service_data.promoCode) {
          // we have promotion
          // Send the first month charge
          sum_charges.push(Number(this.$store.state.service_data.promoCode.firstMonthCharge));
          sum_charges.push(Number(this.$store.state.service_data.promoCode.setupCharge));
        } else {
          sum_charges.push(Number(selected_state.price));
        }
        return (sum_charges.reduce((total, num) => total + num) * 1.1).toFixed(2);
      },
      // Card details section
      card_name: {
        get() {
          return this.$store.state.card_details.cardHolderName;
        },
        set(value) {
          this.$store.commit('card_details/setCardName', value);
        }
      },
      card_number: {
        get() {
          return this.$store.state.card_details.cardNumber;
        },
        set(value) {
          this.$store.commit('card_details/setCardNumber', value);
        },
      },
      card_expiry: {
        get() {
          return this.$store.state.card_details.cardExpiryDate;
        },
        set(value) {
          this.$store.commit('card_details/setCardExpiry', value);
        }
      },
      card_type: {
        get() {
          return this.$store.state.card_details.cardType;
        },
        set(value) {
          this.$store.commit('card_details/setCardType', value);
        }
      },
      card_cvv: {
        get() {
          return this.$store.state.card_details.cardCVV;
        },
        set(value) {
          this.$store.commit('card_details/setCardCvv', value);
        }
      }
    },
    methods: {
      setSubmit: function(event) {
        console.log("Setting event", event);
        self.agreement = event;
        this.$forceUpdate();
      },
      getAddressData: function (addressData, placeResultData, id) {
        console.log("Got address change");
        console.log(addressData);
        console.log(placeResultData);
        console.log(id);
      },
      goBack() {
        this.$store.dispatch('service_data/goBack').then((next) => {
          this.$router.push(next);
        });
      },
      provide_eway_explanation(err) {
        for (let [key, value] of Object.entries(EwayErrors)) {
          if(err.includes(key)) {
            return value;
          }
        }
        // Last ditch effort
        return err;
      },
      validate() {
        this.response_errors = null;
        if(!this.agreement) {
          this.response_errors = this.$t('payment.agreement.required');
          window.scrollTo(0,0);
          return null;
        }
        if(!Object.prototype.hasOwnProperty.call(this.additional_services.addresses,"BillingAddress")) {
          this.response_errors = this.$t('payment.billing_address.required');
          window.scrollTo(0,0);
          return null;
        }
        this.$store.dispatch('service_data/stageSubmission', this.$refs.form).then( next => {
          console.log(next);
          this.$router.push(next);
        }).catch(err => {
          console.log("Got here in validation error");
          if(err.response && [400, 401, 402].includes(err.response.status)) {
            console.log("Saw 400 error");
            if(Object.hasOwnProperty.call(err.response.data, "email")) {
              this.response_errors = "Error with Email: " + err.response.data.email.join(",");
              window.scrollTo(0,0);
            } else if(typeof err.response.data === 'string' && err.response.data.includes("eWAY")) {
              console.log("Got eWay error to parse");
              console.log(this.provide_eway_explanation(err.response.data));
              this.response_errors = "Credit Card Error: " + this.provide_eway_explanation(err.response.data);
              window.scrollTo(0,0);
            } else if(typeof err.response.data === 'string' && err.response.data.includes("Transaction")) {
              console.log("Got eWay error to parse");
              console.log(this.provide_eway_explanation(err.response.data));
              this.response_errors = "Credit Card Error: " + this.provide_eway_explanation(err.response.data);
              window.scrollTo(0,0);
            } else {
              console.log(typeof err.response.data);
              // force to send to sentry
              throw err.response;
            }
          } else if (err == "form_invalid" ) {
            console.log("Form failed validation");
          } else {
            throw err;
          }
        });
      },
      attemptPromoCode() {
        this.response_errors = null;
        this.$store.dispatch('service_data/submitPromocode', this.promocode).then(() => {
          window.scrollTo(0,0);
        }).catch(() => {
          this.response_errors = "Error: Promotion Code was invalid";
          window.scrollTo(0,0);
        });
      },
    }
  }
</script>
<style scoped>
  form h2 {
    font-size: 29px;
    letter-spacing: -1px;
    font-weight: 400;
  }
  form p {
    margin-top: 20px;
    margin-bottom: 0;
  }
  table, td, th {
    border: 0.5px solid #B7B7B7;
  }
  table {
    width: 100%;
    border-radius: 4px 4px 0 0;
    border-spacing: 0;
  }
  table tr {
    text-align: left;
    font-size: 14px;
  }
  table td {
    padding: 8px;
  }

  .bottom-divider {
    height: 1px;
    background: rgb(136, 136, 136);
  }

</style>
